import axios, { AxiosInstance, AxiosResponse } from 'axios';

interface ApiResponse<T = any> {
  status: boolean;
  statusCode: number;
  data?: T;
  message?: string;
}

class ApiClient {
  private instance: AxiosInstance;
  
  constructor(baseURL: string) {
    this.instance = axios.create({ baseURL });
  }

  private getAuthHeaders(accessToken: string, sessionToken: string) {
    return {
      Authorization: `Bearer ${accessToken}:${sessionToken}`
    };
  }

  private handleError(error: any): ApiResponse {
    const statusCode = error.response?.status || 500;
    const statusMsg = error.response?.data?.error || "Connection refused.";

    return {
      status: false,
      statusCode,
      message: statusMsg
    };
  }

  private handleSuccess<T>(response: AxiosResponse<T>): ApiResponse<T> {
    return {
      status: true,
      statusCode: response.status,
      data: response.data
    };
  }

  async get<T>(
    endpoint: string, 
    accessToken: string,
    sessionToken: string,
    params?: Record<string, any>
  ): Promise<ApiResponse<T>> {
    try {
      const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
      const url = `${endpoint}${queryString}`;
      
      const response = await this.instance.get<T>(url, {
        headers: this.getAuthHeaders(accessToken, sessionToken)
      });

      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async post<T>(
    endpoint: string,
    accessToken: string,
    sessionToken: string,
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.instance.post<T>(endpoint, data, {
        headers: this.getAuthHeaders(accessToken, sessionToken)
      });

      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async delete<T>(
    endpoint: string,
    accessToken: string,
    sessionToken: string
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.instance.delete<T>(endpoint, {
        headers: this.getAuthHeaders(accessToken, sessionToken)
      });

      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async patch<T>(
    endpoint: string,
    accessToken: string,
    sessionToken: string,
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.instance.patch<T>(endpoint, data, {
        headers: this.getAuthHeaders(accessToken, sessionToken)
      });

      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }
}

export const apiClient = new ApiClient(process.env.REACT_APP_BACKEND_API_URL || '');